import useAnnouncementPopover from "../hooks/useAnnouncementPopover";
import observeLayoutResize from "../utils/observeLayoutResize";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "../styles/AnnouncementSystem.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const EMPTY_RUNTIME = {
  maintenanceModeEnabled: false,
  maintenanceMessage: "",
  maintenanceDurationMinutes: null,
  maintenanceEndsAt: null,
  nextMaintenanceTransitionAt: null,
  announcements: [],
  seenAnnouncementIds: [],
};

function formatMaintenanceDuration(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hour${hours === 1 ? "" : "s"}${remainingMinutes ? ` ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}` : ""}`;
}

function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return year && month && day ? `${year}-${month}-${day}` : value;
}

function typeLabel(type) {
  return type === "maintenance" ? "Maintenance" : type === "update" ? "Update" : "Notice";
}

function AnnouncementContent({ content, compact = false }) {
  const lines = String(content || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const bullets = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
  if (bullets) {
    return <ul className={compact ? "announcement-banner-list" : "announcement-entry-list"}>
      {lines.map((line, index) => <li key={index}>{line.replace(/^[-*]\s+/, "")}</li>)}
    </ul>;
  }
  return <div className={compact ? "announcement-banner-copy" : "announcement-entry-copy"}>
    {lines.map((line, index) => <p key={index}>{line}</p>)}
  </div>;
}

export default function AnnouncementSystem({ canBypassMaintenance = false }) {
  const [runtime, setRuntime] = useState(EMPTY_RUNTIME);
  const [open, setOpen] = useState(false);
  const [dock, setDock] = useState({ side: "right", y: null });
  const [, redraw] = useState(0);
  const fabRef = useRef(null);
  const dragging = useRef(null);
  const suppressClick = useRef(false);
  const [dragPosition, setDragPosition] = useState(null);
  const snapAnimation = useRef(null);
  useEffect(() => () => snapAnimation.current?.cancel(), []);
  const clampY = (y) => Math.max(16, Math.min(y, Math.max(16, window.innerHeight - 54)));
  useEffect(() => {
    const reset = () => {
      snapAnimation.current?.cancel(); dragging.current = null;
      setDragPosition(null); setDock({ side: "right", y: null });
    };
    window.addEventListener("authSessionChanged", reset);
    return () => window.removeEventListener("authSessionChanged", reset);
  }, []);
  useEffect(() => {
    const resize = () => { setDock((current) => current.y === null ? current : { ...current, y: clampY(current.y) }); redraw((value) => value + 1); };
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  const moveIcon = (event) => {
    const start = dragging.current;
    if (!start) return;
    if (!start.moved && Math.hypot(event.clientX - start.x, event.clientY - start.y) < 5) return;
    start.moved = true;
    suppressClick.current = true;
    setOpen(false);
    start.position = {
      x: Math.max(8, Math.min(start.left + event.clientX - start.x, window.innerWidth - 46)),
      y: clampY(start.top + event.clientY - start.y),
    };
    setDragPosition(start.position);
  };
  const dropIcon = () => {
    const start = dragging.current;
    dragging.current = null;
    if (!start?.moved || !start.position) return;
    const { x, y } = start.position;
    const side = x + 19 < window.innerWidth / 2 ? "left" : "right";
    const destination = side === "left" ? 16 : window.innerWidth - 54;
    let dockY = y;
    const ai = document.querySelector(".admin-ai-orb")?.getBoundingClientRect();
    if (ai && destination < ai.right + 12 && destination + 38 > ai.left - 12 && y < ai.bottom + 12 && y + 38 > ai.top - 12) {
      const choices = [ai.top - 12 - 38, ai.bottom + 12].filter((top) => top >= 16 && top <= window.innerHeight - 54);
      if (choices.length) dockY = choices.reduce((best, top) => Math.abs(top - y) < Math.abs(best - y) ? top : best, choices[0]);
    }
    setDock({ side, y: dockY });
    setDragPosition(null);
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches && fabRef.current?.animate) {
      snapAnimation.current?.cancel();
      const offset = x - destination;
      const verticalOffset = y - dockY;
      const reboundY = Math.max(-7, Math.min(7, -verticalOffset * .08));
      const rebound = Math.max(-7, Math.min(7, -offset * .08));
      snapAnimation.current = fabRef.current.animate([
        { transform: `translate(${offset}px, ${verticalOffset}px)`, offset: 0 },
        { transform: `translate(${rebound}px, ${reboundY}px)`, offset: .72 },
        { transform: `translate(${-rebound * .35}px, ${-reboundY * .35}px)`, offset: .88 },
        { transform: "translate(0, 0)", offset: 1 },
      ], { duration: 420, easing: "ease-out" });
    }
  };
  const panelStyle = useAnnouncementPopover(fabRef, open, `${dock.side}:${dock.y}`);
  const panelRef = useRef(null);
  const bannerRef = useRef(null);
  const maintenanceWasActiveRef = useRef(false);
  const announcements = Array.isArray(runtime.announcements) ? runtime.announcements : [];
  const maintenanceAnnouncements = announcements.filter((item) => item.type === "maintenance");
  const seenIds = useMemo(() => new Set((runtime.seenAnnouncementIds || []).map(Number)), [runtime.seenAnnouncementIds]);
  const unreadCount = announcements.filter((item) => !seenIds.has(Number(item.id))).length;

  useEffect(() => {
    let active = true;
    let timer;
    async function load() {
      try {
        const { data } = await axios.get(`${API_BASE}/api/announcements/runtime`, {
          params: { statusAt: Date.now() },
        });
        if (active) {
          const nextRuntime = { ...EMPTY_RUNTIME, ...data };
          if (maintenanceWasActiveRef.current && !nextRuntime.maintenanceModeEnabled && !canBypassMaintenance) {
            localStorage.clear();
            sessionStorage.clear();
            window.location.replace(`/?refresh=${Date.now()}`);
            return;
          }
          if (nextRuntime.maintenanceModeEnabled) maintenanceWasActiveRef.current = true;
          setRuntime(nextRuntime);
        }
      } catch (error) {
        if (active && error.response?.status !== 401 && error.response?.status !== 403) {
          console.error("Unable to load system announcements:", error);
        }
      }
    }
    const refresh = () => load();
    load();
    timer = window.setInterval(load, 10000);
    window.addEventListener("focus", refresh);
    window.addEventListener("announcements-changed", refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("announcements-changed", refresh);
    };
  }, [canBypassMaintenance]);

  useEffect(() => {
    const transitionAt = runtime.nextMaintenanceTransitionAt;
    if (!transitionAt) return undefined;
    const delay = new Date(transitionAt).getTime() - Date.now() + 300;
    if (!Number.isFinite(delay) || delay <= 0 || delay > 2147000000) return undefined;
    const timer = window.setTimeout(() => window.dispatchEvent(new Event("announcements-changed")), delay);
    return () => window.clearTimeout(timer);
  }, [runtime.nextMaintenanceTransitionAt]);

  useEffect(() => {
    const root = document.documentElement;
    const update = () => root.style.setProperty(
      "--announcement-banner-height",
      `${bannerRef.current?.offsetHeight || 0}px`,
    );
    update();
    const stopObserving = observeLayoutResize([bannerRef.current], update);
    return () => {
      stopObserving();
      root.style.setProperty("--announcement-banner-height", "0px");
    };
  }, [maintenanceAnnouncements.length]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!panelRef.current?.contains(event.target) && !fabRef.current?.contains(event.target)) setOpen(false);
    };
    const closeEscape = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [open]);

  async function togglePanel() {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen || !announcements.length) return;
    const announcementIds = announcements.map((item) => Number(item.id));
    setRuntime((current) => ({ ...current, seenAnnouncementIds: announcementIds }));
    try {
      const { data } = await axios.post(`${API_BASE}/api/announcements/runtime/seen`, { announcementIds });
      setRuntime((current) => ({ ...current, seenAnnouncementIds: data.seenAnnouncementIds || announcementIds }));
    } catch {
      // The optimistic state keeps the badge from distracting the user if the receipt request fails.
    }
  }

  return (
    <>
      {maintenanceAnnouncements.length > 0 && (
        <section className="announcement-system-banner" ref={bannerRef} aria-label="Maintenance announcements">
          {maintenanceAnnouncements.map((item) => (
            <article key={item.id}>
              <div className="announcement-banner-heading">
                <strong>{formatDate(item.announcementDate)}</strong>
                <span className="announcement-type maintenance">Maintenance</span>
                <b>{item.title}</b>
              </div>
              <AnnouncementContent content={item.content} compact />
            </article>
          ))}
        </section>
      )}

      <button
        ref={fabRef}
        type="button"
        data-no-standard-button="true"
        className={`announcement-fab${open ? " open" : ""}${unreadCount ? " unread" : ""}${dragPosition ? " dragging" : ""}`}
        style={dragPosition ? { left: dragPosition.x, right: "auto", top: dragPosition.y } : { left: dock.side === "left" ? 16 : "auto", right: dock.side === "right" ? 16 : "auto", ...(dock.y !== null ? { top: dock.y } : {}) }}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          suppressClick.current = false;
          const rect = event.currentTarget.getBoundingClientRect();
          snapAnimation.current?.cancel();
          dragging.current = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, moved: false };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={moveIcon}
        onPointerUp={(event) => { moveIcon(event); dropIcon(); }}
        onPointerCancel={dropIcon}
        onLostPointerCapture={dropIcon}
        onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } togglePanel(); }}
        aria-label="Open system information"
        aria-expanded={open}
        title="System information — drag to either edge"
      >
        <i className="fas fa-info" aria-hidden="true" />
        {unreadCount > 0 && <span>{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>

      {open && (
        <aside className="announcement-popover" style={panelStyle} ref={panelRef} role="dialog" aria-modal="false" aria-labelledby="system-information-title">
          <header>
            <div>
              <small>System Information</small>
              <h2 id="system-information-title">Updates &amp; Maintenance</h2>
            </div>
            <button type="button" data-no-standard-button="true" onClick={() => setOpen(false)} aria-label="Close system information">&times;</button>
          </header>
          <div className="announcement-popover-body">
            {announcements.length === 0 ? <p className="announcement-empty">No active announcements right now.</p> : announcements.map((item) => (
              <article className="announcement-entry" key={item.id}>
                <div className="announcement-entry-heading">
                  <strong>{formatDate(item.announcementDate)}</strong>
                  <span className={`announcement-type ${item.type}`}>{typeLabel(item.type)}</span>
                </div>
                <h3>{item.title}</h3>
                <AnnouncementContent content={item.content} />
              </article>
            ))}
          </div>
        </aside>
      )}

      {runtime.maintenanceModeEnabled && !canBypassMaintenance && (
        <div className="maintenance-mode-screen" role="alert">
          <div className="maintenance-mode-card">
            <span>Maintenance</span>
            <div className="maintenance-mode-icon" aria-hidden="true">
              <i className="fas fa-tools" />
            </div>
            <h1>System Maintenance in Progress</h1>
            <p>{runtime.maintenanceMessage || "The system is currently under maintenance and will be back shortly. Thank you for your patience."}</p>
            {formatMaintenanceDuration(runtime.maintenanceDurationMinutes) && (
              <p className="maintenance-mode-duration"><i className="far fa-clock" aria-hidden="true" /> Expected duration: <strong>{formatMaintenanceDuration(runtime.maintenanceDurationMinutes)}</strong></p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
